import {
  Button,
  Code,
  Flex,
  IconButton,
  List,
  ListItem,
  Spinner,
  Text,
  UnorderedList,
} from "@chakra-ui/react";
import {
  RiArrowLeftSLine,
  RiArrowRightSLine,
  RiCloseLine,
  RiRefreshLine,
} from "@remixicon/react";
import { useEffect, useState } from "react";

import { DebugEventDetails } from "@/app/components/DebugEventDetails";
import { useDebugProblemOccurrence } from "@/app/data/useDebugProblemOccurrence";
import {
  formatProblemCode,
  getProblemGuidance,
} from "@/app/utils/debugProblems";
import EmptyState from "@/common/components/EmptyState";
import { SectionHeading } from "@/common/components/SectionHeading";
import {
  SidebarContainer,
  SidebarSection,
} from "@/common/components/SidebarLayout";
import TimestampCell from "@/common/components/TimestampCell";
import { FeatureDisplay } from "@/feature/components/FeatureDisplay";
import type {
  DebugProblem,
  DebugProblemOccurrencesQueryType,
  DebugProblemsQueryType,
} from "@reflag/shared/eventAPI";

export type DebugProblemCursor = Required<
  Pick<DebugProblemOccurrencesQueryType, "timestamp" | "eventId">
>;

type Navigation = DebugProblemCursor & { direction: "older" | "newer" };

export function DebugProblemDetails({
  problem,
  filters,
  occurrence,
  onOccurrenceChange,
  onClose,
}: {
  problem: Pick<DebugProblem, "code" | "field" | "operator">;
  filters: Omit<DebugProblemsQueryType, "envId">;
  occurrence?: DebugProblemCursor;
  onOccurrenceChange?: (
    occurrence: DebugProblemCursor | undefined,
    replace?: boolean,
  ) => void;
  onClose: () => void;
}) {
  const [navigation, setNavigation] = useState<Navigation>();
  const cursor =
    navigation ??
    (occurrence ? { ...occurrence, direction: "exact" as const } : undefined);
  const { data, isFetching, isLoading, isError, isPlaceholderData, refetch } =
    useDebugProblemOccurrence({
      ...filters,
      code: problem.code,
      field: problem.field,
      operator: problem.operator,
      ...cursor,
    });
  const group = data?.problem;
  const affectedFlags = data?.affectedFlags ?? [];
  const event = isError ? undefined : data?.event;
  const guidance = getProblemGuidance(problem);

  useEffect(() => {
    if (!event || isFetching || isPlaceholderData || !onOccurrenceChange) {
      return;
    }
    if (
      event.id !== occurrence?.eventId ||
      event.timestamp !== occurrence?.timestamp
    ) {
      onOccurrenceChange(
        { timestamp: event.timestamp, eventId: event.id },
        !navigation,
      );
    }
  }, [
    event,
    isFetching,
    isPlaceholderData,
    navigation,
    occurrence,
    onOccurrenceChange,
  ]);
  const navigate = (direction: Navigation["direction"]) => {
    if (!event || isFetching) return;
    setNavigation({ timestamp: event.timestamp, eventId: event.id, direction });
  };
  const refresh = () => {
    if (navigation || occurrence) {
      setNavigation(undefined);
      onOccurrenceChange?.(undefined);
    } else {
      void refetch();
    }
  };

  return (
    <SidebarContainer>
      <SidebarSection>
        <Flex align="start" gap={2}>
          <SectionHeading flexGrow={1}>
            {formatProblemCode(problem.code)}
          </SectionHeading>
          <IconButton
            aria-label="Close problem details"
            icon={<RiCloseLine size={16} />}
            size="xs"
            variant="ghost"
            onClick={onClose}
          />
        </Flex>
        <Flex gap={2} wrap="wrap">
          <Code wordBreak="break-all">{problem.field}</Code>
          {problem.operator && <Code>{problem.operator}</Code>}
        </Flex>
        {data && (
          <Text color="dimmed" fontSize="sm">
            {(group?.count ?? 0).toLocaleString()} occurrences in the last 24
            hours
          </Text>
        )}
        {group && (
          <Flex align="center" gap={1} color="dimmed" fontSize="sm">
            <Text>Last seen</Text>
            <TimestampCell value={group.lastSeen} leftAlign autoUpdate />
          </Flex>
        )}
      </SidebarSection>
      <SidebarSection>
        <SectionHeading>Affected flags</SectionHeading>
        {isLoading ? (
          <Spinner size="xs" aria-label="Loading affected flags" />
        ) : isError ? (
          <Text color="dimmed" fontSize="sm">
            Affected flags could not be loaded.
          </Text>
        ) : affectedFlags.length === 0 ? (
          <Text color="dimmed" fontSize="sm">
            No affected flags
          </Text>
        ) : (
          <>
            <List spacing={3}>
              {affectedFlags.slice(0, 5).map((feature) => (
                <ListItem key={feature.id}>
                  <FeatureDisplay
                    feature={feature}
                    includeParents={false}
                    size="sm"
                    link
                  />
                </ListItem>
              ))}
            </List>
            {affectedFlags.length > 5 && (
              <Text color="dimmed" fontSize="sm">
                + {(affectedFlags.length - 5).toLocaleString()} more
              </Text>
            )}
          </>
        )}
      </SidebarSection>
      <SidebarSection>
        <SectionHeading>What happened</SectionHeading>
        <Text fontSize="sm">{guidance.explanation}</Text>
      </SidebarSection>
      <SidebarSection>
        <SectionHeading>How to fix</SectionHeading>
        <UnorderedList fontSize="sm" spacing={2}>
          {guidance.fixes.map((fix) => (
            <ListItem key={fix}>{fix}</ListItem>
          ))}
        </UnorderedList>
      </SidebarSection>
      <SidebarSection>
        <Flex align="center" gap={2}>
          <SectionHeading flexGrow={1}>
            {cursor ? "Occurrence" : "Latest occurrence"}
          </SectionHeading>
          {isFetching && <Spinner size="xs" aria-label="Loading occurrence" />}
          <IconButton
            aria-label="Refresh problem"
            icon={<RiRefreshLine size={16} />}
            size="xs"
            variant="ghost"
            isDisabled={isFetching}
            onClick={refresh}
          />
        </Flex>
        <Flex align="center" justify="space-between" gap={2}>
          <Button
            size="xs"
            variant="outline"
            leftIcon={<RiArrowLeftSLine size={16} />}
            isDisabled={!data?.hasOlder || !event || isFetching || isError}
            onClick={() => navigate("older")}
          >
            Older
          </Button>
          {event && (
            <TimestampCell value={event.timestamp} leftAlign autoUpdate />
          )}
          <Button
            size="xs"
            variant="outline"
            rightIcon={<RiArrowRightSLine size={16} />}
            isDisabled={!data?.hasNewer || !event || isFetching || isError}
            onClick={() => navigate("newer")}
          >
            Newer
          </Button>
        </Flex>
      </SidebarSection>
      {isLoading || isError || !event ? (
        <EmptyState
          title={
            occurrence
              ? "Occurrence no longer available"
              : "No matching occurrence in the last 24 hours"
          }
          description={
            occurrence
              ? "This occurrence may have expired from the 24-hour window or no longer matches the current filters. Load the latest occurrence for this problem instead."
              : "Occurrences can fall outside the 24-hour window while you browse. Refresh to load the latest occurrence."
          }
          action={
            occurrence && (
              <Button size="sm" variant="outline" onClick={refresh}>
                Load latest occurrence
              </Button>
            )
          }
          error="The occurrence could not be loaded."
          isLoading={isLoading}
          isError={isError}
          onRetry={() => void refetch()}
        />
      ) : (
        <DebugEventDetails
          event={event}
          showEventType={false}
          showRuleResults={false}
          showFlagDisplay
        />
      )}
    </SidebarContainer>
  );
}
